import { Box, BoxProps, Heading, SelectInput, smBumps } from '@wrisk/ui-components/src'
import { isMatch, isNil } from 'lodash-es'
import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react'

import { CoverLevelOptionSelector, Quote } from '../../../../../domain'
import { useExcesses } from '../../../../../hooks/excess'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { logger } from '../../../../../infrastructure/logging'
import { ExcessCombination } from '../../../../../state/configuration'
import { generateSlug } from './generateSlug'
import { textGenerator } from './textGenerator'

interface ExcessSelectionProps extends BoxProps {
  tKey: TKeyBuilder
  quote: Quote
  selection: CoverLevelOptionSelector
  onChange?: (excess: ExcessCombination) => void
  loading?: boolean
}

const key = 'excess-selection'

export const ExcessSelection: FunctionComponent<ExcessSelectionProps> = ({
  tKey,
  quote,
  selection,
  onChange,
  loading,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const { coverLevels } = useExcesses(quote)

  const coverLevel = selection.coverLevel
    ? coverLevels?.[selection.coverLevel]
    : undefined

  const values = useMemo(() => {
    const generateText = textGenerator(t)
    return (
      coverLevel?.map((combination) => ({
        value: generateSlug(combination),
        text: generateText(combination),
        combination,
      })) ?? []
    )
  }, [coverLevel])

  const header = t(tKey(key, 'header'), { defaultValue: null })
  const value = values.find((it) => isMatch(quote.voluntaryExcesses, it.combination))

  useEffect(() => {
    if (isNil(value) && values.length) {
      logger.warn(`Invalid excess combination found`)
      onChange?.(values[0].combination)
    }
  }, [value, values])

  const handleChange = useCallback(
    (slug: string) => {
      const value = values.find((it) => it.value === slug)

      if (value) {
        onChange?.(value.combination)
      } else {
        logger.error(`Unknown combination: ${slug}`)
      }
    },
    [values],
  )

  return values.length ? (
    <Box display='flex' flexDirection='column' gap={smBumps} width={1} {...props}>
      {header && (
        <Heading variant='h4' as='h4' typoSize='md'>
          {header}
        </Heading>
      )}

      <SelectInput<string>
        value={value?.value}
        values={values}
        onChange={handleChange}
        loading={loading}
      />
    </Box>
  ) : null
}
